import { registerPlugin } from '@capacitor/core';
import { absolutizeUploadUrl, bearerHeaders, resolveUrl, type HuntMediaInput } from './api';
import type { GpsStoredPoint } from './karta/gpsUtils';

/**
 * Bilder och video från jakten ur telefonens bildbibliotek. Klipp från Ray-Ban Meta hamnar här
 * när Meta AI-appen har sparat dem i Bilder (inställning i Meta AI-appen). Nativ del:
 * ios/App/App/HuntMediaPlugin.swift (söker själv i Bilder efter jaktens tid) och
 * android/…/HuntMediaPlugin.java (findMedia öppnar systemets bildväljare – jägaren väljer).
 */

export type LibraryAccess = 'authorized' | 'limited' | 'denied' | 'notDetermined';

export interface LibraryItem {
  /** Bildbibliotekets id (PHAsset.localIdentifier) */
  id: string;
  type: 'image' | 'video';
  takenAt: number;
  /** Sekunder, 0 för bilder */
  duration: number;
  lat?: number;
  lng?: number;
  filename?: string;
  thumbnail?: string;
}

export interface NetworkStatus { connected: boolean; wifi: boolean; expensive: boolean; constrained: boolean }

export interface UploadOptions {
  /** Mindre bilder (2560 px) och 1080p-video. */
  compress?: boolean;
  maxBytes?: number;
  /** false = avbryt hellre (NO_WIFI) än att använda mobildata. */
  allowCellular?: boolean;
}

/** Nativa metoder. getAccess, presentLimitedPicker och networkStatus finns från A3 (äldre byggen saknar dem). */
export const HuntMediaNative = registerPlugin<{
  requestAccess(): Promise<{ status: LibraryAccess }>;
  getAccess(): Promise<{ status: LibraryAccess }>;
  presentLimitedPicker(): Promise<{ count: number }>;
  networkStatus(): Promise<NetworkStatus>;
  findMedia(options: { from: number; to: number; limit?: number }): Promise<{ status: LibraryAccess; items: LibraryItem[] }>;
  uploadMedia(options: { id: string; url: string; headers: Record<string, string> } & UploadOptions): Promise<{ url: string; type: string }>;
}>('HuntMedia');
const HuntMedia = HuntMediaNative;

/** Marginal före start och efter slut, så klipp tagna precis runt jakten kommer med. */
export const HUNT_MEDIA_MARGIN_MS = 5 * 60 * 1000;
/** Längsta avstånd i tid till närmaste spårpunkt för att positionen ska räknas som säker. */
const TRACK_MAX_GAP_MS = 5 * 60 * 1000;

export function isHuntMediaAvailable(): boolean {
  const cap = (window as any).Capacitor;
  // Webben laddas från qvenari.com, så äldre appbyggen utan pluginet får samma kod.
  const platform = cap?.getPlatform?.();
  return !!cap?.isNativePlatform?.() && (platform === 'ios' || platform === 'android') && !!cap.isPluginAvailable?.('HuntMedia');
}

/** Android: findMedia öppnar bildväljaren i stället för att söka, så den körs bara när jägaren ber om det. */
export function huntMediaUsesPicker(): boolean {
  return isHuntMediaAvailable() && (window as any).Capacitor?.getPlatform?.() === 'android';
}

export async function requestLibraryAccess(): Promise<LibraryAccess> {
  return (await HuntMedia.requestAccess()).status;
}

export async function findLibraryMedia(from: number, to: number): Promise<LibraryItem[]> {
  return (await HuntMedia.findMedia({ from, to, limit: 300 })).items;
}

/** Laddar upp direkt från telefonen (filen passerar aldrig webbvyn) och returnerar filens adress. */
export async function uploadLibraryItem(id: string, options: UploadOptions = {}): Promise<string> {
  const res = await HuntMedia.uploadMedia({ id, url: resolveUrl('/api/upload'), headers: bearerHeaders(), ...options });
  return absolutizeUploadUrl(res.url);
}

/** Låter jägaren välja fler bilder när appen bara har begränsad åtkomst. */
export async function presentLimitedLibraryPicker(): Promise<void> {
  await HuntMedia.presentLimitedPicker();
}

// ── Markens område (A3) ─────────────────────────────────────────────────────

/** Ring av [lng, lat] som i GeoJSON. */
export type Ring = [number, number][];
export interface MarkArea { rings: Ring[]; center: { lat: number; lng: number } | null }
export type AreaMatch = 'inside' | 'near' | 'outside' | 'unknown';

/** Så långt utanför markgränsen räknas en bild ändå som tagen på marken (gränsfall, eftersök, samling). */
export const NEAR_MARK_M = 1500;
/** Utan ritad gräns: så nära markens kartpunkt. */
const NEAR_CENTER_M = 4000;

/** Ytterringarna i markens GeoJSON (Feature, FeatureCollection, Polygon eller MultiPolygon). */
export function parseMarkArea(coordinatesJson: unknown, center?: { lat?: number | null; lng?: number | null } | null): MarkArea {
  const rings: Ring[] = [];
  const isPos = (p: unknown): p is [number, number] => Array.isArray(p) && typeof p[0] === 'number' && typeof p[1] === 'number';
  const visit = (g: any) => {
    if (!g || typeof g !== 'object') return;
    if (g.type === 'FeatureCollection') (g.features || []).forEach(visit);
    else if (g.type === 'Feature') visit(g.geometry);
    else if (g.type === 'GeometryCollection') (g.geometries || []).forEach(visit);
    else if (g.type === 'Polygon' && Array.isArray(g.coordinates?.[0])) {
      const r = g.coordinates[0].filter(isPos);
      if (r.length >= 3) rings.push(r);
    } else if (g.type === 'MultiPolygon') {
      for (const poly of g.coordinates || []) {
        const r = (poly?.[0] || []).filter(isPos);
        if (r.length >= 3) rings.push(r);
      }
    }
  };
  try { visit(typeof coordinatesJson === 'string' ? JSON.parse(coordinatesJson) : coordinatesJson); } catch { /* ingen gräns */ }
  const c = center?.lat != null && center?.lng != null ? { lat: center.lat, lng: center.lng } : null;
  return { rings, center: c };
}

function insideRing(lat: number, lng: number, ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Meter till närmaste kant (platt jord – räcker för några kilometer). */
function metersToRing(lat: number, lng: number, ring: Ring): number {
  const kx = 111320 * Math.cos((lat * Math.PI) / 180);
  const ky = 110540;
  let best = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const [ax, ay] = ring[i];
    const [bx, by] = ring[(i + 1) % ring.length];
    const x1 = (ax - lng) * kx, y1 = (ay - lat) * ky, x2 = (bx - lng) * kx, y2 = (by - lat) * ky;
    const dx = x2 - x1, dy = y2 - y1;
    const len = dx * dx + dy * dy;
    const f = len === 0 ? 0 : Math.max(0, Math.min(1, -(x1 * dx + y1 * dy) / len));
    best = Math.min(best, Math.hypot(x1 + f * dx, y1 + f * dy));
  }
  return best;
}

export function matchArea(pos: { lat: number; lng: number } | null, area: MarkArea): AreaMatch {
  if (!pos) return 'unknown';
  if (area.rings.length > 0) {
    if (area.rings.some(r => insideRing(pos.lat, pos.lng, r))) return 'inside';
    return area.rings.some(r => metersToRing(pos.lat, pos.lng, r) <= NEAR_MARK_M) ? 'near' : 'outside';
  }
  if (area.center) {
    const dLat = (pos.lat - area.center.lat) * 110540;
    const dLng = (pos.lng - area.center.lng) * 111320 * Math.cos((pos.lat * Math.PI) / 180);
    return Math.hypot(dLat, dLng) <= NEAR_CENTER_M ? 'near' : 'outside';
  }
  return 'unknown';
}

export type MatchedItem = LibraryItem & { area: AreaMatch };

/** Var bilden togs i förhållande till marken: bildens GPS, annars jägarens spår. */
export function classifyLibraryItems(items: LibraryItem[], area: MarkArea, track: GpsStoredPoint[]): MatchedItem[] {
  return items.map(i => {
    const pos = i.lat != null && i.lng != null ? { lat: i.lat, lng: i.lng } : positionFromTrack(track, i.takenAt);
    return { ...i, area: matchArea(pos, area) };
  });
}

/**
 * Får laddas upp automatiskt: tagen på eller nära marken. Utan position bara om marken saknar
 * både gräns och kartpunkt (då finns inget att jämföra med och tiden får avgöra).
 */
export function isAutoEligible(item: MatchedItem, area: MarkArea): boolean {
  if (item.area === 'inside' || item.area === 'near') return true;
  return item.area === 'unknown' && area.rings.length === 0 && !area.center;
}

/** Förvalt i väljaren: allt utom det som bevisligen togs långt från marken. */
export const isPreselected = (item: MatchedItem) => item.area !== 'outside';

/**
 * Position vid tidpunkten t utifrån jägarens GPS-spår: interpolerar mellan punkterna före och
 * efter, eller tar närmaste punkt om t ligger utanför spåret. null om närmaste punkt är för långt bort i tid.
 */
export function positionFromTrack(track: GpsStoredPoint[], t: number): { lat: number; lng: number } | null {
  if (track.length === 0) return null;
  const pts = [...track].sort((a, b) => a.t - b.t);
  const after = pts.findIndex(p => p.t >= t);
  if (after === 0 || after === -1) {
    const p = after === 0 ? pts[0] : pts[pts.length - 1];
    return Math.abs(p.t - t) <= TRACK_MAX_GAP_MS ? { lat: p.lat, lng: p.lng } : null;
  }
  const a = pts[after - 1];
  const b = pts[after];
  if (t - a.t > TRACK_MAX_GAP_MS && b.t - t > TRACK_MAX_GAP_MS) return null;
  const f = b.t === a.t ? 0 : (t - a.t) / (b.t - a.t);
  return { lat: a.lat + (b.lat - a.lat) * f, lng: a.lng + (b.lng - a.lng) * f };
}

/** Det som sparas för ett uppladdat objekt: bildens egen GPS i första hand, annars spåret. */
export function toHuntMediaInput(item: LibraryItem, url: string, track: GpsStoredPoint[]): HuntMediaInput {
  const base: HuntMediaInput = { url, mediaType: item.type, takenAt: item.takenAt, sourceId: item.id };
  if (item.lat != null && item.lng != null) return { ...base, lat: item.lat, lng: item.lng, positionSource: 'photo' };
  const pos = positionFromTrack(track, item.takenAt);
  return pos ? { ...base, ...pos, positionSource: 'track' } : base;
}
